import React, { forwardRef, useRef } from 'react'

import Box from '../box'
import ScrollView, {
  type ScrollEvent,
  type ScrollViewNode,
  type ScrollViewProps
} from '../scroll-view'
import type { LayoutEvent } from '../_support'

/* -----------------------------------------------------------------------------
 * FlatList
 *
 * react-native's FlatList as react-native-web 0.17 left it once its cells had
 * rendered: a ScrollView with each item in its own View, separators inside the
 * cells, and header, footer or empty component around them. Every item renders
 * (the lists here are paged and short), and onEndReached keeps its rules: when
 * the end is closer than `onEndReachedThreshold` visible lengths, once per
 * content length. A list that grows with its content is always at its end, so
 * it asks for the next page each time its content grows.
 * -------------------------------------------------------------------------- */

type ListComponent = React.ComponentType<any> | React.ReactElement | null

export type FlatListProps<Item> = Omit<ScrollViewProps, 'children'> & {
  data: ReadonlyArray<Item> | null | undefined
  renderItem: (info: { item: Item; index: number }) => React.ReactNode
  keyExtractor?: (item: Item, index: number) => string
  ItemSeparatorComponent?: React.ComponentType<any> | null
  ListHeaderComponent?: ListComponent
  ListFooterComponent?: ListComponent
  ListEmptyComponent?: ListComponent
  onEndReached?: (info: { distanceFromEnd: number }) => void
  onEndReachedThreshold?: number
  // react-native's virtualization settings: every item renders here
  initialNumToRender?: number
  maxToRenderPerBatch?: number
}

const renderComponent = (Component: ListComponent) => {
  if (!Component || React.isValidElement(Component)) return Component
  const ListItem = Component as React.ComponentType<any>
  return <ListItem />
}

const defaultKeyExtractor = (item: any, index: number) =>
  item?.key != null
    ? item.key
    : item?.id != null
    ? String(item.id)
    : String(index)

const FlatList = forwardRef(
  <Item,>(
    {
      data,
      renderItem,
      keyExtractor = defaultKeyExtractor,
      ItemSeparatorComponent,
      ListHeaderComponent,
      ListFooterComponent,
      ListEmptyComponent,
      onEndReached,
      onEndReachedThreshold,
      initialNumToRender,
      maxToRenderPerBatch,
      onLayout,
      onScroll,
      onContentSizeChange,
      ...props
    }: FlatListProps<Item>,
    ref: React.ForwardedRef<ScrollViewNode>
  ) => {
    const metrics = useRef({ contentLength: 0, visibleLength: 0, offset: 0 })
    const sentEndForContentLength = useRef(0)
    const latest = useRef({ onEndReached, onEndReachedThreshold })
    latest.current = { onEndReached, onEndReachedThreshold }

    const maybeCallOnEndReached = () => {
      const { onEndReached, onEndReachedThreshold } = latest.current
      const { contentLength, visibleLength, offset } = metrics.current
      const distanceFromEnd = contentLength - visibleLength - offset
      const threshold =
        onEndReachedThreshold != null
          ? onEndReachedThreshold * visibleLength
          : 2
      if (
        onEndReached &&
        distanceFromEnd < threshold &&
        contentLength !== sentEndForContentLength.current
      ) {
        sentEndForContentLength.current = contentLength
        onEndReached({ distanceFromEnd })
      } else if (distanceFromEnd > threshold) {
        // scrolling away from the end and back asks again
        sentEndForContentLength.current = 0
      }
    }

    const handleLayout = (event: LayoutEvent) => {
      metrics.current.visibleLength = event.nativeEvent.layout.height
      onLayout?.(event)
      maybeCallOnEndReached()
    }

    const handleContentSizeChange = (width: number, height: number) => {
      onContentSizeChange?.(width, height)
      metrics.current.contentLength = height
      maybeCallOnEndReached()
    }

    const handleScroll = (event: ScrollEvent) => {
      onScroll?.(event)
      const { contentOffset, contentSize, layoutMeasurement } =
        event.nativeEvent
      metrics.current = {
        contentLength: contentSize.height,
        visibleLength: layoutMeasurement.height,
        offset: contentOffset.y
      }
      maybeCallOnEndReached()
    }

    const items = data ?? []

    return (
      <ScrollView
        ref={ref}
        scrollEventThrottle={50}
        {...props}
        onLayout={handleLayout}
        onContentSizeChange={handleContentSizeChange}
        onScroll={handleScroll}
      >
        {ListHeaderComponent ? (
          <Box>{renderComponent(ListHeaderComponent)}</Box>
        ) : null}
        {items.length > 0
          ? items.map((item, index) => (
              <Box key={keyExtractor(item, index)}>
                {renderItem({ item, index })}
                {ItemSeparatorComponent && index < items.length - 1 ? (
                  <ItemSeparatorComponent />
                ) : null}
              </Box>
            ))
          : renderComponent(ListEmptyComponent)}
        {ListFooterComponent ? (
          <Box>{renderComponent(ListFooterComponent)}</Box>
        ) : null}
      </ScrollView>
    )
  }
) as <Item>(
  props: FlatListProps<Item> & { ref?: React.Ref<ScrollViewNode> }
) => React.ReactElement

export default FlatList
